import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { styled } from "@mui/material";
import { useTranslate } from "../../../hooks/useTranslate";
import { usePatient } from "../../../state/patientContext";
import {
  DiseaseMatrices,
  extractMetricsForSuggestionAndAllDiseases,
} from "fe-common/utils/zoomUtil";
import { DeniedToggle } from "./deniedToggle";
import { PatientColorMap } from "../../../layout/patientColorMap";
import { GenericButton } from "../../uiControllers/genericButton";
import { DiseaseReasoningListItem } from "./diseaseReasoningListItem";
import { featureTo2Lines } from "fe-common/logic";
import { ORPHANS_CLUSTER_ID } from "shared";
import { DemoZoomContent } from "./useDemoReasoningController";
import { QualifiedFeature } from "fe-common/graphql";

//region [[ Styles ]]

const MobileSuggestionReasoningBoxView = styled("div")({});

export const MobileReasoningTopArea = styled("div")({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
});

export const MobileReasoningTopTitle = styled("div")({
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
  width: "100%",
  flexWrap: "wrap",
  "& > * ": {
    marginRight: "3px",
  },
  marginBottom: "15px",
  fontSize: "19px",
  fontWeight: 700,
});

export const ReferencesBox = styled("div")({
  display: "flex",
  padding: "24px",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
});

export const ReferencesBoxText = styled("div")({
  color: PatientColorMap.Primary,
  fontSize: "12px",
  fontWeight: 400,
  lineHeight: "17px",
  textAlign: "center",
});

export const ReferencesButtonContainer = styled("div")({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-around",
  marginTop: "14px",
  width: "100%",
  alignItems: "center",
});

export const ReferencesButton = styled(GenericButton)({
  "&.generic-button": {
    color: PatientColorMap.White,
    background: PatientColorMap.Primary,
    height: "auto",
    "& .patient-MuiButton-label": {
      fontSize: "12px",
      lineHeight: "12px",
      fontWeight: 500,
      padding: "7px 5px",
    },
    maxHeight: "40px",
  },
});

const DiseasesReasoningList = styled("div")({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  "& .generic-button": {
    width: "30%",
  },
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface MobileSuggestionReasoningBoxProps extends DemoZoomContent {
  onDiseaseSelected: (d: DiseaseMatrices) => void;
  onShowGraph: (qf: QualifiedFeature) => void;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]
const DEFAULT_DESKTOP_ITEMS_COUNT_TO_SHOW = 2;
export const MobileSuggestionReasoningBox = ({
  ...props
}: MobileSuggestionReasoningBoxProps) => {
  const { translate } = useTranslate();

  const { selectedSuggestion, conversation } = usePatient();

  const [yesNo, setYesNo] = useState(true);

  const toggleWhenConfirmed = useCallback(
    (event, selected) => setYesNo(!selected),
    [setYesNo],
  );

  // Get sugestions for selected questions to get DD
  const suggestion = conversation?.suggestions?.suggestions?.find(
    (sg) => sg.suggestion.id === selectedSuggestion?.qualifiedFeature?.id,
  );

  const suspectedDiseases = useMemo(
    () =>
      conversation?.suggestions?.diseases.map((d) => ({
        name: d.disease.name,
        id: d.disease.id,
        prob: d.prob,
        crId: d.crId,
      })),
    [conversation],
  );

  const post = React.useCallback(
    (m) => (yesNo ? m.postIfTrue : m.postIfFalse),
    [yesNo],
  );

  const [showAll, setShowAll] = useState(!props.desktop);

  const clusters = conversation?.suggestions?.clusters;

  const metrics = useMemo(() => {
    const _metrics =
      suggestion &&
      extractMetricsForSuggestionAndAllDiseases(suspectedDiseases, suggestion);

    _metrics?.sort((a, b) => post(b) - post(a));

    const ret: DiseaseMatrices[] = [];
    //Keep a disease per cluster with the best post(yes/no).
    const addedDiseases = new Set();
    _metrics?.forEach((d) => {
      const diseaseClusters = clusters?.filter((c) => c.crIds.includes(d.crId));
      diseaseClusters?.forEach((c) => {
        const name = c.title.feature.name;
        if (
          !addedDiseases.has(name) &&
          c.title.feature.id !== ORPHANS_CLUSTER_ID
        ) {
          ret.push({ ...d, diseaseName: name });
          addedDiseases.add(name);
        }
      });
    });

    return ret.length ? ret : _metrics;
  }, [suggestion, suspectedDiseases, post, clusters]);

  if (!suggestion) return <div>Clinical Insight Not Available</div>;

  let diseaseName = featureTo2Lines(selectedSuggestion?.qualifiedFeature).join(
    " ",
  );

  if (selectedSuggestion.qualifiedFeature?.qualifiers?.length) {
    diseaseName += `(${selectedSuggestion.qualifiedFeature?.qualifiers
      .map((q) => q.name.toLocaleLowerCase())
      .join(",")}) `;
  }

  const topTitle = `${translate("WHY_DID_THIS_WAS_ASKED")}
  ${diseaseName} ${translate("NOW")}?`;

  if (props.desktop && props.setTitle) props.setTitle(topTitle);

  return (
    <MobileSuggestionReasoningBoxView>
      <MobileReasoningTopArea className={"top-area"}>
        {!props.desktop && (
          <MobileReasoningTopTitle className={"top-title"}>
            {topTitle}
          </MobileReasoningTopTitle>
        )}
      </MobileReasoningTopArea>
      <ReferencesBox className={"references-box"}>
        <ReferencesBoxText>
          {translate("SUGGESTION_REASONING_EXPLANATION_TEXT")
            .split("#FINDING_NAME#")
            .map((t, idx, arr) => (
              <React.Fragment key={idx}>
                {t} {idx + 1 === arr.length ? null : diseaseName}
              </React.Fragment>
            ))}
        </ReferencesBoxText>
        <ReferencesButtonContainer>
          <DeniedToggle
            className={"denied-toggle"}
            diseaseName={diseaseName}
            checked={!yesNo}
            onChange={toggleWhenConfirmed}
          />
          {!props.desktop && (
            <ReferencesButton
              label={translate("SHOW_REFERENCES")}
              onClick={props.onReferencesClick}
            />
          )}
        </ReferencesButtonContainer>
      </ReferencesBox>
      <DiseasesReasoningList className={"disease-reasoning-list"}>
        {metrics
          .slice(
            0,
            showAll ? metrics.length : DEFAULT_DESKTOP_ITEMS_COUNT_TO_SHOW,
          )
          .map((diseaseReasoning, idx) => (
            <DiseaseReasoningListItem
              key={idx}
              disease={diseaseReasoning}
              onDiseaseReasoningClick={props.onDiseaseSelected}
              isPositive={yesNo}
              onShowGraph={props.onShowGraph}
            />
          ))}
        {props.desktop &&
          metrics.length > DEFAULT_DESKTOP_ITEMS_COUNT_TO_SHOW && (
            <GenericButton
              onClick={() => setShowAll((s) => !s)}
              label={showAll ? "Show Less" : "Show All"}
            />
          )}
      </DiseasesReasoningList>
    </MobileSuggestionReasoningBoxView>
  );
};
